'use client';

import type { RowData } from '@tanstack/react-table';
import type { Column, Table } from '@/lib/table-features';
import { useTranslations } from 'next-intl';
import * as React from 'react';

import { DataTableChipsFilter } from '@/components/ui/table/data-table-chips-filter';
import { DataTableDateFilter } from '@/components/ui/table/data-table-date-filter';
import { DataTableDateTimeRangeFilter } from '@/components/ui/table/data-table-datetime-range-filter';
import { DataTableFacetedFilter } from '@/components/ui/table/data-table-faceted-filter';
import { DataTableInfiniteFilter } from '@/components/ui/table/data-table-infinite-filter';
import { DataTableSliderFilter } from '@/components/ui/table/data-table-slider-filter';
import { DataTableViewOptions } from '@/components/ui/table/data-table-view-options';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Loader2 } from 'lucide-react';
import type { InfiniteSelectConfig } from '@/types/base-form';

interface DataTableToolbarProps<
  TData extends RowData
> extends React.ComponentProps<'div'> {
  table: Table<TData>;
  /** Column-id → InfiniteSelectConfig for columns with variant='infiniteMultiSelect' */
  infiniteConfigs?: Record<string, InfiniteSelectConfig>;
  /**
   * Permission required to render the View Columns button. Convention:
   * `{group}.view_filter`. When unset the button is always visible.
   */
  viewFilterPermission?: string;
  /**
   * Render the View Columns button. Defaults to `true`; set `false` on
   * surfaces that drive a virtual (no-display) table purely for its filters.
   */
  showViewOptions?: boolean;
}

export function DataTableToolbar<TData extends RowData>({
  table,
  infiniteConfigs,
  viewFilterPermission,
  showViewOptions = true,
  children,
  className,
  ...props
}: DataTableToolbarProps<TData>) {
  const tc = useTranslations('common');
  const isFiltered = table.state.columnFilters.length > 0;
  const isFilterPending = table.options.meta?.isFilterPending ?? false;

  // Read fresh each render: TanStack returns a new array from getAllColumns()
  // only when table.options.columns changes, so this is cheap. Memoizing on
  // [table] alone would freeze on the initial column instances, missing later
  // meta updates (e.g. async-loaded filter options).
  const columns = table
    .getAllColumns()
    .filter((column) => column.getCanFilter());

  const onReset = React.useCallback(() => {
    table.resetColumnFilters();
  }, [table]);

  return (
    <div
      role='toolbar'
      aria-orientation='horizontal'
      className={cn(
        'flex w-full items-start justify-between gap-2 p-1',
        className
      )}
      {...props}
    >
      <div className='flex flex-1 flex-wrap items-center gap-2'>
        {columns.map((column) => (
          <DataTableToolbarFilter
            key={column.id}
            column={column}
            isFilterPending={isFilterPending}
            infiniteConfig={
              column.id ? infiniteConfigs?.[column.id] : undefined
            }
          />
        ))}
        {isFiltered && (
          <Button
            aria-label='Reset filters'
            variant='outline'
            size='sm'
            className='border-dashed'
            onClick={onReset}
          >
            <Cross2Icon />
            {tc('resetFilters')}
          </Button>
        )}
      </div>
      <div className='flex items-center gap-2'>
        {children}
        {showViewOptions && (
          <DataTableViewOptions
            table={table}
            viewFilterPermission={viewFilterPermission}
          />
        )}
      </div>
    </div>
  );
}
interface DataTableToolbarFilterProps<TData extends RowData> {
  column: Column<TData>;
  isFilterPending: boolean;
  infiniteConfig?: InfiniteSelectConfig;
}

function DataTableToolbarFilter<TData extends RowData>({
  column,
  isFilterPending,
  infiniteConfig
}: DataTableToolbarFilterProps<TData>) {
  const columnMeta = column.columnDef.meta;

  if (!columnMeta?.variant) return null;

  switch (columnMeta.variant) {
    case 'text':
      return (
        <DataTableChipsFilter
          column={column}
          isFilterPending={isFilterPending}
          placeholder={columnMeta.placeholder ?? columnMeta.label}
          hint={columnMeta.searchHint}
        />
      );

    case 'number':
      return (
        <div className='relative'>
          <Input
            type='number'
            inputMode='numeric'
            placeholder={columnMeta.placeholder ?? columnMeta.label}
            value={(column.getFilterValue() as string) ?? ''}
            onChange={(event) => column.setFilterValue(event.target.value)}
            className={cn(
              'h-8 w-[120px]',
              isFilterPending && 'pe-8',
              columnMeta.unit && 'w-36 pe-14'
            )}
          />
          {isFilterPending ? (
            <span className='pointer-events-none absolute end-0 top-0 bottom-0 flex items-center pe-2'>
              <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
            </span>
          ) : columnMeta.unit ? (
            <span className='bg-accent text-muted-foreground pointer-events-none absolute end-0 top-0 bottom-0 flex items-center rounded-e-md px-2 text-sm'>
              {columnMeta.unit}
            </span>
          ) : null}
        </div>
      );

    case 'range':
      return (
        <DataTableSliderFilter
          column={column}
          title={columnMeta.label ?? column.id}
        />
      );

    case 'date':
    case 'dateRange':
      return (
        <DataTableDateFilter
          column={column}
          title={columnMeta.label ?? column.id}
          multiple={columnMeta.variant === 'dateRange'}
        />
      );

    case 'dateTimeRange':
      return (
        <DataTableDateTimeRangeFilter
          column={column}
          title={columnMeta.label ?? column.id}
        />
      );

    case 'select':
    case 'multiSelect':
      return (
        <DataTableFacetedFilter
          column={column}
          title={columnMeta.label ?? column.id}
          options={columnMeta.options ?? []}
          multiple={columnMeta.variant === 'multiSelect'}
          icon={columnMeta.icon}
        />
      );

    case 'infiniteMultiSelect':
      return infiniteConfig ? (
        <DataTableInfiniteFilter
          column={column}
          title={columnMeta.label ?? column.id}
          infiniteConfig={infiniteConfig}
        />
      ) : null;

    default:
      return null;
  }
}
